import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../Atoms/PatientFile"
import { useModalitiesProvider } from "../../../../../../../Providers/ModalitiesProvider"
import { useEnvironmentVariablesProvider } from "../../../../../../../Providers/EnvironmentVariablesProvider"
import { DataSource } from "../../../Types/DataSource"
import { analyticTracesAtom } from "../../../Atoms/Analytics"
import { getSupportedRenderStrategies, TraceOption } from "../../../Types/Trace"
import { isAnalyticTraceEnabled } from "./analyticTraceFlags"

type useTraceOptionsProps = {
    includeAnalytics: boolean
}

export const useTraceOptions = (options?: useTraceOptionsProps ): TraceOption[] => {
    const { patientModalities } = useRecoilValue(currentPatientFileInfoAtom)
    const modalitiesProvider = useModalitiesProvider()
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const analyticTraceOptions = useRecoilValue(analyticTracesAtom)

    const traceOptions = Object.entries(modalitiesProvider.modalities)
        .filter(([modality]) => !modality.startsWith('EEG'))
        .map(([name, modalityObj]): TraceOption => {
            let source = ''

            if (modalityObj && modalityObj.file_name) {
                const parts = modalityObj.file_name.split(',')

                if (parts.length > 2) {
                    source = parts[parts.length - 2]
                }
            }

            return {
                label: name,
                source,
                value: {
                    name,
                    dataKey: name,
                    rawDataKey: name,
                    dataSource: DataSource.CURRENT_PATIENT,
                    units: "",
                    isCompositePart: false,
                },
                supportedRenderStrategies: getSupportedRenderStrategies(
                    { rawDataKey: name, onDemandAnalysis: undefined },
                    { environmentVariables, fileName: modalityObj?.file_name },
                ),
                highlight: patientModalities.includes(name)
            }
        })

    if (options?.includeAnalytics) {
        const filteredAnalytics = analyticTraceOptions.filter(trace => isAnalyticTraceEnabled(trace, environmentVariables))

        traceOptions.push(...filteredAnalytics.map(traceDataConfig => ({
            label: traceDataConfig.name,
            value: traceDataConfig,
            supportedRenderStrategies: getSupportedRenderStrategies(traceDataConfig, { environmentVariables, fileName: undefined }),
            highlight: false,
            source: ""
        })))
    }

    traceOptions.sort((a, b) => {
        const aIsAvailable = a.highlight
        const bIsAvailable = b.highlight

        if (aIsAvailable && !bIsAvailable) {
            return -1 // a comes first
        }
        if (!aIsAvailable && bIsAvailable) {
            return 1 // b comes first
        }

        // If both are available or both are not available, sort alphabetically by label
        return a.label.localeCompare(b.label)
    })

    return traceOptions
}
